/**
 * MonthlyDevizeCard - Card cu totaluri lunare devize
 * Extras din DevizTab.jsx pentru modularitate
 */
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS_RO = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];

export function MonthlyDevizeCard({
  monthlyDevize,
  selectedYear,
  selectedMonth,
  setSelectedYear,
  setSelectedMonth,
  onMonthPrev,
  onMonthNext
}) {
  return (
    <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-sky-50" data-testid="monthly-devize-card">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-700" />
            <div>
              <p className="text-sm text-blue-700 font-medium">Valoare Totala Devize</p>
              <p className="text-2xl font-bold text-blue-900" data-testid="devize-total-value">
                {monthlyDevize ? `${monthlyDevize.total_cu_tva.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON` : "..."}
              </p>
              {monthlyDevize && (
                <p className="text-xs text-blue-500">
                  {monthlyDevize.count} devize | fara TVA: {monthlyDevize.total_fara_tva.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onMonthPrev} className="h-8 w-8 p-0" data-testid="month-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Select
              value={`${selectedYear}-${selectedMonth}`}
              onValueChange={(v) => {
                const [y, m] = v.split("-");
                setSelectedYear(parseInt(y));
                setSelectedMonth(parseInt(m));
              }}
            >
              <SelectTrigger className="w-[170px] h-8 text-sm bg-white border-blue-200" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => {
                  const m = i + 1;
                  return (
                    <SelectItem key={`${selectedYear}-${m}`} value={`${selectedYear}-${m}`}>
                      {MONTHS_RO[i]} {selectedYear}
                    </SelectItem>
                  );
                })}
                {selectedYear > 2025 && Array.from({ length: 12 }, (_, i) => {
                  const m = i + 1;
                  const prevYear = selectedYear - 1;
                  return (
                    <SelectItem key={`${prevYear}-${m}`} value={`${prevYear}-${m}`}>
                      {MONTHS_RO[i]} {prevYear}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={onMonthNext} className="h-8 w-8 p-0" data-testid="month-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {/* Mini devize list */}
        {monthlyDevize && monthlyDevize.devize.length > 0 && (
          <div className="mt-3 pt-3 border-t border-blue-200 space-y-1">
            {monthlyDevize.devize.map((d, i) => (
              <div key={d.numarDeviz || `deviz-${i}`} className="flex items-center justify-between text-sm px-2 py-1 rounded hover:bg-blue-100/50">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">{d.numarDeviz}</Badge>
                  <span className="text-slate-600 truncate max-w-[200px]">{d.beneficiar || "-"}</span>
                </div>
                <span className="font-semibold text-blue-800">{d.totalCuTVA.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
